import { Howl, Howler, HowlOptions } from "howler";
import hidden from "./hidden";
import storage from "./storage";
const IS_IOS = () => {
  return /iphone|ipad|ipod|mac/i.test(navigator.userAgent);
};

const DEFAULT_HTML5 = IS_IOS || navigator.userAgent.indexOf("OPR") !== -1;

const DEFAULT_STORAGE_KEY = "KL-audio";
interface MhyAudioOptions {
  src: string[] | string;
  index?: number;
  loop?: boolean;
  volume?: number;
  preload?: boolean;
  autoplay?: boolean;
  cache?: boolean;
  storageKey?: string;
  storageKeySuffix?: string;
  html5?: boolean;
  fade?: [number, number];
  sprite?: HowlOptions["sprite"];
  seekInterval?: number;
  onPlayed?: (isplayed: boolean) => void;
  onPlayError?: any;
  onEnd?: (index: number) => void;
  onPlaying?: (state: { cur: number; total: number; progress: number }) => void;
}

class eventAudio {
  private sounds: Howl[];
  private src: string[];
  private loop: boolean;
  private preload: boolean;
  private autoplay: boolean;
  private cache: boolean;
  private storageKey: string;
  private storageKeySuffix?: string;
  private index: number;
  private _isplayed: boolean;
  private onPlayed?: (isplayed: boolean) => void;
  private onPlayError?: () => void;
  private onPlaying?: (state: {
    cur: number;
    total: number;
    progress: number;
  }) => void;
  private fade: [number, number];
  private volume: number;
  private hidden: any;
  private html5: any;
  private onEnd?: (index: number) => void;
  private sprite: HowlOptions["sprite"];
  private progressTimer: any;
  private seekInterval: number;
  private playing: boolean;
  private canClick: boolean;

  constructor(options: MhyAudioOptions) {
    const {
      src,
      index = 0,
      autoplay = true,
      loop = true,
      preload = true,
      cache = true,
      storageKey,
      storageKeySuffix,
      onPlayed,
      onEnd,
      onPlayError,
      onPlaying,
      fade = [1000, 1000],
      volume = 1,
      html5 = DEFAULT_HTML5,
      sprite = {},
      seekInterval = 100,
    } = options;
    this.src = Array.isArray(src) ? src : [src];
    this.sounds = [];
    this.loop = loop;
    this.autoplay = autoplay;
    this.preload = preload;
    this.cache = cache;
    this.storageKey = storageKey || DEFAULT_STORAGE_KEY;
    this.storageKeySuffix = storageKeySuffix;
    this.index = index;
    this._isplayed = false;
    this.isplayed = false;
    this.onPlayed = onPlayed;
    this.onEnd = onEnd;
    this.onPlayError = onPlayError;
    this.onPlaying = onPlaying;
    this.volume = volume;
    this.html5 = html5;
    this.fade = html5 ? [0, 0] : fade;
    this.hidden = null;
    this.sprite = sprite;
    this.seekInterval = seekInterval;
    this.progressTimer = null;
    this.playing = false;
    this.canClick = true;
    this.init();
  }

  get sound() {
    return this.sounds[this.index];
  }

  get isplayed() {
    return this._isplayed;
  }

  set isplayed(val) {
    this._isplayed = val;
    this.triggerProgressTimer(val);
  }

  init() {
    if (!this.src[0]) {
      throw "props src is required";
    }
    this.isplayed = this.cache
      ? storage.get(this.storageKey, this.storageKeySuffix) ?? this.autoplay
      : this.autoplay;
    this.emitPlayed();
    this.sounds = this.src.map((src, idx) => this.registHowler(src, idx));

    if (this.isplayed) {
      if (!this.sound) {
        throw "error src'index";
      }
      this._play();
    }

    this.bindHiddenEvent();
  }

  registHowler(src, idx) {
    const audio = new Howl({
      src,
      loop: this.html5 ? false : this.loop,
      preload: this.preload,
      volume: this.volume,
      html5: this.html5,
      sprite: this.sprite,
      onend: () => {
        if (!this.loop) {
          this.isplayed = false;
          this.emitPlayed();
          this.onEnd && this.onEnd(idx);
        } else if (this.html5) {
          const sound = this.sounds[idx];
          sound.unload();

          const newSound = this.registHowler(src, idx);
          this.sounds[idx] = newSound;

          // 兼容fade的场景
          setTimeout(() => {
            if (this.isplayed && idx === this.index) {
              if (!this.sound) {
                throw "error src'index";
              }
              this._play();
            }
          }, this.fade[1] + 1);
        }
      },
      onplayerror: () => {
        this.playing = false;
        this.onPlayError && this.onPlayError();
        const sound = this.sounds[this.index];
        if (sound) {
          sound.once("unlock", () => {
            if (!this.playing) {
              this._play();
              this.playing = true;
            }
          });
        }
      },
    });

    return audio;
  }

  emitPlayed() {
    this.onPlayed && this.onPlayed(this.isplayed);
  }

  private bindHiddenEvent() {
    const cb = (isshow) => {
      if (isshow) {
        if (this.isplayed) {
          this._play();
        }
      } else {
        this._pause();
      }
    };
    this.hidden = hidden(cb);
    this.hidden?.on();
  }

  async switchSrcIndex(index) {
    if (!this.sounds[index]) {
      throw "error src'index";
    }

    if (!this.canClick) return;
    this.canClick = false;

    if (this.isplayed) {
      await this._stop();
    }
    this.index = index;

    if (this.isplayed) {
      this._play();
    }

    this.canClick = true;
  }

  async play(spriteId?: string) {
    const spriteKeys = this.sprite ? Object.keys(this.sprite) : [];
    const isSpriteAudio = spriteKeys.length !== 0;
    if (this.isplayed && !isSpriteAudio) {
      return;
    }
    if (this.isplayed && isSpriteAudio) {
      await this._stop();
    }
    if (isSpriteAudio && spriteId && !spriteKeys.includes(spriteId)) {
      throw Error(`音效【${spriteId}】不存在`);
    }
    this.isplayed = true;
    this.emitPlayed();
    this.cache &&
      storage.set(this.storageKey, this.isplayed, 0, this.storageKeySuffix);
    this._play(spriteId);
  }

  pause() {
    if (!this.isplayed) {
      return;
    }
    this.isplayed = false;
    this.emitPlayed();
    this.cache &&
      storage.set(this.storageKey, this.isplayed, 0, this.storageKeySuffix);
    this._pause();
  }

  private _play(spriteId?: string) {
    const sound = this.sounds[this.index];
    if (!sound) {
      return;
    }
    const func = () => {
      sound.once("play", () => {
        sound.fade(0, this.volume, this.fade[0]);
      });
      if (!sound.playing() && this.isplayed) {
        sound.play(spriteId);
      }
    };
    if (sound.state() === "loaded") {
      func();
      return;
    }
    sound.once("load", func);
    /**
     * 部分机型的 UA 会被 Howler 误判导致加载失败，此时再调用 play 尝试下是否可以播放（仅 HTML Audio 有效）
     */
    if (this.html5) {
      sound.once("loaderror", (_, msg) => {
        /**
         * 由于编解码不支持 mp3 导致的 loaderror
         * 从 caniuse 来看就没有不支持 mp3 的浏览器了，哪怕有也不在我们的兼容性列表中了
         */
        if (
          msg === "No codec support for selected audio sources." &&
          !Howler.codecs(".mp3")
        ) {
          func();
        }
      });
    }
    sound.load();
  }

  private _pause() {
    this.sounds.forEach((item) => {
      item.pause();
    });
  }

  stop() {
    this.isplayed = false;
    this.emitPlayed();
    this.cache &&
      storage.set(this.storageKey, this.isplayed, 0, this.storageKeySuffix);
    return this._stop();
  }

  changeVolume(val) {
    this.volume = val;
    this.sounds.forEach((item) => {
      item.volume(val);
    });
  }

  _stop() {
    return new Promise((resolve) => {
      if (!this.sound) {
        resolve(true);
        return;
      }
      this.sound.once("fade", () => {
        this.sound.stop();
        setTimeout(() => {
          resolve(true);
        }, 0);
      });
      this.sound.fade(this.volume, 0, this.fade[1]);
    });
  }

  unload() {
    this.sounds.forEach((item) => {
      item.unload();
    });
    this.hidden?.off();
    clearInterval(this.progressTimer);
  }

  triggerProgressTimer(val) {
    if (val && this.onPlaying) {
      this.getCurSeek(this.onPlaying);
      this.progressTimer = setInterval(() => {
        this.getCurSeek(this.onPlaying);
      }, this.seekInterval);
    } else {
      clearInterval(this.progressTimer);
    }
  }

  getCurSeek(cb) {
    try {
      if (!this.sound) {
        cb({ cur: 0, total: 0, progress: 0 });
        return;
      }

      const cur = this.sound.seek();
      const total = this.sound.duration();
      cb({ cur, total, progress: Math.floor(((cur as number) / total) * 100) });
    } catch {
      // noop
    }
  }
}

export { eventAudio, Howler, Howl };
